import CountUp from "react-countup";
import { Emcrd } from "../../cp/emissionRights/PriceAnalysis";
import React, {useEffect, useRef, useState} from "react";
import { EmissionService } from "../../../services/cp/EmissionService";
import moment from "moment";
import { toast } from "react-toastify";
import ArcSelectbox, { SelectDataModel } from "../../../components/arc/ArcSelectbox";
import { useAppDispatch } from "../../../store/hook";
import { setEmissionsList } from "../../../reducers/cpSlice";
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { HighchartsOptionsUtil } from "../../../utils/highchartsOptionsUtil";
import { parseNumberWithCommaDecimal } from "../../../utils/stringUtil";
import {DashboardModuleProps} from "../../../model/db/dashboardModuleProps";
import DashboardCard from "./DashboardCard";

const ERROR_MESSAGE = 'common.message.error.system';

export default function EmcrdPrcInfo(props: Readonly<DashboardModuleProps>) {
  const dispatch = useAppDispatch();
  const [data, setData] = useState<Emcrd[]>([]);
  const [lastData, setLastData] = useState<Emcrd | undefined>(undefined);
  const [isinCd, setIsinCd] = useState<Emcrd["isinCd"]>('');
  const [emsList, setEmsList] = useState<Emcrd[]>([]);
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const [chartOptions, setChartOptions] = useState<Highcharts.Options | null>(null);

  useEffect(() => {
    EmissionService.getInstance()
    .getEmission()
    .then((res) => {
      if(res?.data) {
        const uniqueItems = Array.from(
            new Set(
                res.data.map((item: Emcrd) =>
                    JSON.stringify({ itmNm: item.itmNm, isinCd: item.isinCd })
                )
            )
        ).map(value => {
          return JSON.parse(value as string);
        });

        dispatch(setEmissionsList(uniqueItems));
        setEmsList(uniqueItems);
        setIsinCd(uniqueItems.length > 0 ? uniqueItems[0].isinCd : "");
      }
    })
    .catch(() => toast.error(ERROR_MESSAGE));
  }, [dispatch]);

  useEffect(() => {
    if (!isinCd) return;

    EmissionService.getInstance()
    .getEmissionByIsinCd(isinCd)
    .then((res) => {
      if (!res.data || !Array.isArray(res.data.data)) return;

      const tempData = res.data.data;

      if (tempData.length > 0) {
        setData(tempData);
        setLastData(tempData[0]);
      }
    })
    .catch(() => toast.error(ERROR_MESSAGE));
  }, [isinCd]);

  const emsListOption = (): SelectDataModel[] => {
    return emsList.map((val: Emcrd) => ({
      label: val.itmNm,
      value: val.isinCd,
    }));
  };

  useEffect(() => {
    if (data.length === 0) return;

    const ohlc = data.map((emcrd: Emcrd) => {
      const datetime: number = moment(emcrd.baseYmd, "YYYYMMDD").unix() * 1000;
      return [
        datetime,
        Number(emcrd.mktPrc) || emcrd.clsPrc,
        Number(emcrd.hgPrc) || emcrd.clsPrc,
        Number(emcrd.lwPrc) || emcrd.clsPrc,
        emcrd.clsPrc,
      ];
    }).sort((a, b) => (a[0] as number) - (b[0] as number));

    const volume = data.map((emcrd: Emcrd) => {
      const datetime: number = moment(emcrd.baseYmd, "YYYYMMDD").unix() * 1000;
      return [datetime, emcrd.trdVol];
    }).sort((a, b) => (a[0] as number) - (b[0] as number));

    const options: Highcharts.Options = {
      chart: {
        height: 450,
      },
      title: { text: '' },
      time: HighchartsOptionsUtil.locale.kr.time,
      exporting: { enabled: false },
      credits: { enabled: false },
      rangeSelector: {
        x: 15,
        buttons: [
          {
            type: 'day',
            count: 7,
            text: '7일'
          },
          {
            type: 'month',
            count: 1,
            text: '1개월'
          },
          {
            type: 'month',
            count: 3,
            text: '3개월'
          },
          {
            type: 'year',
            count: 1,
            text: '1년'
          },
          {
            type: 'ytd',
            text: ' 올해'
          },
          {
            type: 'all',
            text: '전체'
          }],
        inputEnabled: false
      },
      tooltip: HighchartsOptionsUtil.locale.kr.stockChart.tooltip("원"),
      legend: {
        enabled: true,
        floating: true,
        symbolHeight: 10,
        align: "right",
        verticalAlign: "top",
        y: -42,
        itemStyle: {
          color: '#555',
          fontSize: '0.75rem'
        },
        itemHoverStyle: {
          color: '#333',
          fontSize: '0.75rem'
        }
      },
      plotOptions: {
        candlestick: {
          color: '#2970cd',
          lineColor: '#2970cd',
          lineWidth: 1,
          upColor: '#E0564E',
          upLineColor: '#E0564E',
        },
        column: {
          color: '#b8b8bb',
          borderRadius: 0,
        },
      },
      xAxis: {
        type: "datetime",
        dateTimeLabelFormats: HighchartsOptionsUtil.locale.kr.dateTimeLabelFormats,
        showFirstLabel: true,
        showLastLabel: true,
        lineColor: '#999',
        lineWidth: 1,
        labels: {
          style: {
            color: '#444',
            fontSize: '0.7rem',
          },
        }
      },
      yAxis: [
        {
          opposite: false,
          showLastLabel: true,
          height: "70%",
          offset: 0,
          title: {
            text: '(원)',
            style: {
              color: '#444',
              fontSize: '0.65rem',
            },
            align: 'high',
            rotation: 0,
            offset: 0,
            y: -25,
            x: -5,
          },
          min: 0,
          softMax: 10,
          lineColor: '#999',
          lineWidth: 1,
          gridLineColor: '#CCC',
          gridLineWidth: 1,
          gridLineDashStyle: 'ShortDash',
          labels: {
            y: 3,
            distance: 7,
            style: {
              color: '#444',
              fontSize: "0.7rem",
            },
            formatter: function (this: any) {
              return parseNumberWithCommaDecimal(this.value);
            },
          },
        },
        {
          opposite: false,
          showLastLabel: true,
          top: "78%",
          height: "22%",
          offset: 0,
          title: {
            text: '(톤)',
            style: {
              color: '#444',
              fontSize: '0.65rem',
            },
            align: 'high',
            rotation: 0,
            offset: 0,
            y: 98,
            x: -5,
          },
          min: 0,
          softMax: 10,
          lineColor: '#999',
          lineWidth: 1,
          gridLineColor: '#CCC',
          gridLineWidth: 1,
          gridLineDashStyle: 'ShortDash',
          labels: {
            y: 6,
            distance: 7,
            style: {
              color: '#444',
              fontSize: "0.7rem",
            },
            formatter: function (this: any) {
              return parseNumberWithCommaDecimal(this.value);
            },
          },
        },
      ],
      navigator: {
        xAxis: {
          dateTimeLabelFormats: HighchartsOptionsUtil.locale.kr.dateTimeLabelFormats,
          labels: {
            style: {
              color: '#444',
              fontSize: '0.7rem',
            },
          },
        },
      },
      series: [
        {
          type: 'candlestick',
          name: lastData?.itmNm,
          data: ohlc,
        },
        {
          type: 'column',
          name: '거래량',
          data: volume,
          yAxis: 1,
        },
      ],
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              xAxis: {
                labels: {
                  style: {
                    fontSize: '0.5rem',
                  },
                },
              },
              yAxis: [
                {
                  title: {
                    y: -10,
                    style: {
                      fontSize: '0.5rem',
                    },
                  },
                  labels: {
                    style: {
                      fontSize: '0.5rem',
                    },
                  },
                },
                {
                  title: {
                    y: 88,
                    style: {
                      fontSize: '0.5rem',
                    },
                  },
                  labels: {
                    style: {
                      fontSize: '0.5rem',
                    },
                  },
                },
              ],
              legend: {
                floating: false,
                align: 'center',
                verticalAlign: 'bottom',
                layout: 'horizontal',
                y: 0,
                itemStyle: {
                  fontSize: '0.6rem',
                },
              },
              tooltip: {
                style: {
                  fontSize: '0.6rem',
                },
              },
              navigator: {
                xAxis: {
                  labels: {
                    style: {
                      fontSize: '0.5rem',
                    },
                  },
                },
              },
            },
          },
        ],
      },
    };

    setChartOptions(options);
  }, [data, lastData]);

  return (
    <DashboardCard
      title="배출권 시세 정보"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/cp/emiss/price"
      dashboardConfig={props.dashboardConfig}
    >
      <div className="inner-flex1">
        <div className="tit-select">
          <h4 className="tit">종목선택</h4>
          <div>
            <ArcSelectbox
              name={"분류"}
              className="select-group InpSel"
              selectedValue={isinCd}
              onChange={(event) => setIsinCd(event.target.value)}
              options={emsListOption()}
              title="배출권 종목"
            />
          </div>
        </div>
        <div className="round-table2">
          <table>
            <caption>탄소중립 모의예측 : 배출권 사업 지원 서비스, 배출권 구매</caption>
            <colgroup>
              <col style={{width: '20%'}}/>
            </colgroup>
            <thead>
              <tr>
                <th scope="col">현재가(원)</th>
                <th scope="col">대비(원)</th>
                <th scope="col">시가 (원)</th>
                <th scope="col">고가 (원)</th>
                <th scope="col">저가 (원)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <CountUp end={Number(lastData?.clsPrc) ?? 0} duration={0.5}>
                    {({countUpRef}) => <strong ref={countUpRef}/>}
                  </CountUp>
                </td>
                <td>
                  <CountUp end={Number(lastData?.vs) ?? 0} duration={0.5}>
                    {({countUpRef}) => <strong ref={countUpRef}/>}
                  </CountUp>
                </td>
                <td>
                  <CountUp end={Number(lastData?.mktPrc) ?? 0} duration={0.5}>
                    {({countUpRef}) => <strong ref={countUpRef}/>}
                  </CountUp>
                </td>
                <td><CountUp end={Number(lastData?.hgPrc) ?? 0} duration={0.5}>
                  {({countUpRef}) => <strong ref={countUpRef}/>}
                </CountUp></td>
                <td>
                  <CountUp end={Number(lastData?.lwPrc) ?? 0} duration={0.5}>
                    {({countUpRef}) => <strong ref={countUpRef}/>}
                  </CountUp>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="chart">
          {chartOptions && (
            <HighchartsReact
              ref={chartComponentRef}
              highcharts={Highcharts}
              options={chartOptions}
              constructorType={"stockChart"}
            />
          )}
        </div>
      </div>
    </DashboardCard>
  );
}
